import { useMutation, useQuery, useQueryClient } from 'react-query';
import { apiGetJson, apiPatchJson } from '~/core/dapi';
import { DAPI_HOST } from '../../../../../config';
import useBookingData from '~/routes/pass/hooks/useBookingData';
import { hoursBeforeNow } from '~/core/util/time';
import { VisitSummaryStatuses } from '~/components/AccountGetsMarried/constants/visitSummary';

type VisitSummary = {
  // The visit summary content (markdown)
  summary_text: string;

  // Whether the summary contains sensitive minor data (if it does, it should not be displayed to Mary)
  contains_sensitive_minor_data: boolean;

  // The most recent time the summary was downloaded by Mary to her account
  downloaded_by_patient_date: Date;

  // The most recent time the summary was revised
  revision_date: Date;

  // The signature (with title and credentials) for the summary author
  signature: string;
};

/**
 * Hook to get a mutation for marking a visit summary as downloaded by the patient
 *
 * @param bookingId - the booking the visit summary is for
 * @returns the mutation
 */
export const useVisitSummaryDownloadedMutation = (bookingId: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    () => {
      let nowTimestamp = new Date().toISOString();
      return apiPatchJson(`${DAPI_HOST}/v1/patient-visit-summaries/update/${bookingId}`, {
        downloaded_by_patient_date: nowTimestamp,
      });
    },
    {
      onSuccess: (data) => {
        queryClient.setQueryData(['visit-summaries', bookingId], data);
      },
    }
  );
};

/**
 * Hook to get a mutation for marking a visit summary as viewed by the patient
 *
 * @param bookingId - the booking the visit summary is for
 * @returns the mutation
 */
export const useVisitSummaryViewedMutation = (bookingId: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    () => {
      let nowTimestamp = new Date().toISOString();
      return apiPatchJson(`${DAPI_HOST}/v1/patient-visit-summaries/update/${bookingId}`, {
        viewed_by_patient_date: nowTimestamp,
      });
    },
    {
      onSuccess: (data) => {
        queryClient.setQueryData(['visit-summaries', bookingId], data);
      },
    }
  );
};

export const useVisitSummaryQuery = (bookingId: string) => {
  const { isLoading, data } = useQuery(['visit-summaries', bookingId], () => {
    return apiGetJson<VisitSummary>(`${DAPI_HOST}/v1/patient-visit-summaries/${bookingId}`);
  });

  return { isLoading, data };
};

export const useVisitSummary: (bookingId: string) => any = (bookingId: string) => {
  const { booking } = useBookingData(bookingId);
  const { isLoading, data } = useVisitSummaryQuery(bookingId);

  const { dischargedTimestamp } = booking;
  const bookingOver24HoursOld = hoursBeforeNow(dischargedTimestamp) > 24;

  let summaryStatus = bookingOver24HoursOld
    ? VisitSummaryStatuses.Missing
    : VisitSummaryStatuses.Pending;

  let summaryMarkdown = data?.data?.summary_text;
  const summaryContainsProtectedData = data?.data?.contains_sensitive_minor_data;

  if (summaryContainsProtectedData) {
    // This should be unnecessary (endpoint should not return markdown in this case).
    // Just being super duper careful to ensure that no summary with protected data is shown to Mary.
    summaryMarkdown = '';
  }

  const summaryExists = summaryMarkdown || summaryContainsProtectedData;
  let summarySignature = '';

  if (summaryExists) {
    const downloadedByPatientDate = data?.data?.downloaded_by_patient_date;
    const revisionDate = data?.data?.revision_date;
    summarySignature = data?.data?.signature || '';
    if (!downloadedByPatientDate) {
      summaryStatus = VisitSummaryStatuses.Downloadable;
    } else if (revisionDate && downloadedByPatientDate < revisionDate) {
      summaryStatus = VisitSummaryStatuses.Updatable;
    } else {
      summaryStatus = VisitSummaryStatuses.Viewable;
    }
  }

  if (!summaryExists) {
    // Temporarily always set summary missing instead of pending in this case,
    // since SteadyMD will not always be providing summaries during the training phase
    // for their providers for this visit summary feature. Note: It probably seems obvious that there are other,
    // more elegant ways I could've done this - I basically just chose to do it this way in
    // order to deceive the compiler. Travis build will fail unless the compiler "thinks"
    // we sometimes return a Pending status from this hook.
    summaryStatus = VisitSummaryStatuses.Missing;
  }

  return {
    isLoading,
    summaryContainsProtectedData,
    summaryStatus,
    summaryExists,
    summaryMarkdown,
    summarySignature,
  };
};
